import { ChangeEvent, FC, useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, Pagination, Typography } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";

import { useReportEntries, useApproveAll, useGetPendingAdvances, useReportEntryApprovals } from "../service/reportEntry";
import { RefundReportTableMobile } from "./RefundReportTableMobile";
import ApprovalCallaborator from "./atoms/ApprovalCallaborator";
import { RefundReportFilter } from "./RefundReportFilter";
import { RefundReportTable } from "./RefundReportTable";
import { statusFilterOptions, getAdvanceFilterValue } from "../utils/filter";
import { ReportEntry } from "../@types/ReportEntry";
import Collaborator from "../@types/Collaborator";
import { FilterValues } from "../@types/filter";
import DefaultPage from "../templates/Default";
import { useUsers } from "../service/user";
import { Status } from "../enums/Status";
import Loading from "./atoms/Loading";
import Modal from "./atoms/Modal";
import { PendingAdvancesAlert } from "./molecules/PendingAdvancesAlert";
import RefundCycleBanner from "./molecules/RefundCycleBanner";

interface RefundReportViewInfo {
  account?: Collaborator;
  groupByCollaborator: boolean;
  selfRefundsOnly: boolean;
  title: string;
  showTotalValue: boolean;
  showExportFilesButtons: boolean;
  showSendToButtons: boolean;
  showAddReportEntryButton: boolean;
  includeAdvances?: boolean;
}

interface ResetFiltersProps {
  useApprovalDefault?: boolean;
  clearUrl?: boolean;
}

const FIRST_BACKEND_PAGE = 0;
const COLLABORATOR_LIST_FETCH_SIZE = 1500;

const RefundReportView: FC<RefundReportViewInfo> = ({
  account,
  groupByCollaborator,
  selfRefundsOnly,
  title,
  showTotalValue,
  showExportFilesButtons,
  showSendToButtons,
  showAddReportEntryButton,
  includeAdvances = false,
}) => {
  const location = useLocation();
  const isApprovalsPage = location.pathname === "/approvals";
  const isSearchPage = location.pathname === "/search";
  const [openModal, setOpenModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<string>("createdDate,desc");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1024);

  const isInitialized = useRef(false);
  const prevPathname = useRef(location.pathname);

  const [filterValues, setFilterValues] = useState<FilterValues>({
    collaboratorName: searchParams.get("collaboratorName") ?? "",
    collaborator: searchParams.get("collaborator")
      ? ({
          id: parseInt(searchParams.get("collaborator")!),
          name: searchParams.get("collaboratorName") ?? "",
        } as Collaborator)
      : null,
    ownerName: searchParams.get("ownerName") ?? "",
    costCenter: searchParams.get("costCenter") ?? "",
    statusFilter: searchParams.get("statusFilter") ? statusFilterOptions[searchParams.get("statusFilter")!] : null,
    startDate: searchParams.get("startDate") ? searchParams.get("startDate")! : null,
    endDate: searchParams.get("endDate") ? searchParams.get("endDate")! : null,
    expenseCategory: null,
    ownerApproved: undefined,
    financialApproved: undefined,
    ownerApprovedDateUntil: null,
    ownerApprovedDateAfter: null,
    financialApproveDateUntil: null,
    financialApproveDateAfter: null,
    createDateUntil: null,
    createDateAfter: null,
    isAdvance: getAdvanceFilterValue(searchParams.get("isAdvance") ?? "", isMobile),
    clientRefund: searchParams.get("clientRefund") === "true" ? true : searchParams.get("clientRefund") === "false" ? false : null,
  });

  // draftFilters: estado de edição local para /approvals (sem disparar fetch)
  const [draftFilters, setDraftFilters] = useState<FilterValues>({ ...filterValues });

  const shouldFetchUsers = true;
  const shouldFetchReportEntries =
    !!filterValues.collaborator?.id || location.pathname.includes("/search") || selfRefundsOnly || isApprovalsPage;
  const reportEntriesFetchSize = isMobile ? 50 : 20;
  const collaboratorListFilters = isApprovalsPage
    ? { ...filterValues, collaborator: null }
    : filterValues;

  const {
    data: filteredReportEntries,
    isLoading: loadingReportEntries,
    isFetching: isFetchingReportEntries,
  } = useReportEntries(currentPage - 1, reportEntriesFetchSize, filterValues, shouldFetchReportEntries, sort);

  const {
    data: pendingAdvances,
    isFetching: isFetchingPendingAdvances,
    refetch: refetchPendingAdvances,
  } = useGetPendingAdvances({
    ...filterValues,
    isAdvance: true,
    statusFilter: statusFilterOptions.all,
  });

  const {
    data: collaborators,
    isLoading: loadingCollaborators,
    isFetching: isFetchingCollaborators,
  } = useUsers(
    {
      ...filterValues,
      collaborator: selfRefundsOnly ? account ?? null : null,
      page: FIRST_BACKEND_PAGE,
      size: COLLABORATOR_LIST_FETCH_SIZE,
    },
    shouldFetchUsers && !isApprovalsPage,
  );

  const {
    data: approvalCollaborators,
    isLoading: loadingApprovalCollaborators,
    isFetching: isFetchingApprovalCollaborators,
  } = useReportEntryApprovals(
    FIRST_BACKEND_PAGE,
    COLLABORATOR_LIST_FETCH_SIZE,
    collaboratorListFilters,
    shouldFetchUsers && isApprovalsPage,
  );

  const collaboratorPageData = isApprovalsPage ? approvalCollaborators : collaborators;
  const isLoadingCollaboratorList = isApprovalsPage
    ? loadingApprovalCollaborators && isFetchingApprovalCollaborators
    : loadingCollaborators && isFetchingCollaborators;

  const { mutate: approveAll } = useApproveAll();

  const hasPendingApproval = (reports: ReportEntry[]) => {
    return reports.some((report) => {
      if (account?.financialSupport)
        return (
          (report.reportEntryStatus == Status.ON_APPROVAL || report.reportEntryStatus == Status.REFUSED) &&
          report.approvedByFinancial == null
        );
      else
        return (
          (report.reportEntryStatus == Status.ON_APPROVAL || report.reportEntryStatus == Status.REFUSED) &&
          report.approvedByOwner == null &&
          account?.isApprover
        );
    });
  };


  const resetFilters = useCallback(({ clearUrl = false }: ResetFiltersProps = {}) => {
    const newFilterValues: FilterValues = {
      collaboratorName: selfRefundsOnly ? account?.name ?? "" : "",
      collaborator: selfRefundsOnly ? account ?? null : null,
      ownerName: "",
      costCenter: "",
      statusFilter: null,
      startDate: null,
      endDate: null,
      expenseCategory: null,
      ownerApproved: undefined,
      financialApproved: undefined,
      isAdvance: undefined,
      ownerApprovedDateUntil: null,
      ownerApprovedDateAfter: null,
      financialApproveDateUntil: null,
      financialApproveDateAfter: null,
      createDateUntil: null,
      createDateAfter: null,
      clientRefund: null,
    };

    // Automatic set default filters for approvals page
    if (isApprovalsPage) {
      newFilterValues.statusFilter = account?.financialSupport
        ? { ...statusFilterOptions.all }
        : { ...statusFilterOptions.pendingOwner };
    }

    setDraftFilters(newFilterValues);

    if (clearUrl) {
      setFilterValues(newFilterValues);
      setSearchParams({}, { replace: true });
      return;
    }

    triggerFilterUpdate(newFilterValues);
  }, [selfRefundsOnly, account, location.pathname, setSearchParams]);

  const handleClearFilters = useCallback(() => {
    resetFilters({ 
      useApprovalDefault: isApprovalsPage, 
      clearUrl: true 
    });
  }, [resetFilters, isApprovalsPage]);

  const triggerFilterUpdate = (newFilterValues: FilterValues) => {
    const updatedFilters = { ...newFilterValues };

    if (selfRefundsOnly) {
      updatedFilters.collaborator = account ?? null;
    }

    if (updatedFilters.statusFilter?.key === "pendingOwner") {
      updatedFilters.ownerApproved = undefined;
      updatedFilters.financialApproved = true;
    }

    if (updatedFilters.statusFilter?.key === "pendingFinance") {
      updatedFilters.ownerApproved = true;
      updatedFilters.financialApproved = false;
    }

    if (updatedFilters.statusFilter?.key === "all") {
      updatedFilters.ownerApproved = undefined;
      updatedFilters.financialApproved = undefined;
    }

    setFilterValues(updatedFilters);
    if (!selfRefundsOnly) {
      updateSearchParams(updatedFilters);
    }
  };

  const shouldEnableAllButtons = () => {
    if (location.pathname === "/") {
      return false;
    }

    if (isApprovalsPage && filterValues.collaborator?.id) {
      return true;
    }

    if (isSearchPage) {
      return true;
    }

    return false;
  };

  const updateSearchParams = useCallback(
    (newFilterValues: FilterValues) => {
      setSearchParams((prev) => {
        const filters = [
          { key: "collaborator", value: newFilterValues.collaborator?.id },
          { key: "collaboratorName", value: newFilterValues.collaboratorName },
          { key: "statusFilter", value: newFilterValues.statusFilter?.key },
          { key: "startDate", value: newFilterValues.startDate?.toString() },
          { key: "endDate", value: newFilterValues.endDate?.toString() },
          { key: "costCenter", value: newFilterValues.costCenter },
          {
            key: "isAdvance",
            value: newFilterValues.isAdvance ? "true" : newFilterValues.isAdvance === false ? "false" : undefined,
          },
          {
            key: "clientRefund",
            value: newFilterValues.clientRefund ? "true" : newFilterValues.clientRefund === false ? "false" : undefined,
          },
        ];

        filters.forEach(({ key, value }) => {
          if (value) {
            prev.set(key, value.toString());
          } else {
            prev.delete(key);
          }
        });

        return prev;
      });
    },
    [setSearchParams],
  );

  // ✅ ADICIONAR: Memorizar a função triggerFilterUpdate
  const memoizedTriggerFilterUpdate = useCallback(
    (newFilterValues: FilterValues) => {
      const updatedFilters = { ...newFilterValues };

      if (selfRefundsOnly) {
        updatedFilters.collaborator = account ?? null;
      }

      if (updatedFilters.statusFilter?.key === "pendingOwner") {
        updatedFilters.ownerApproved = undefined;
        updatedFilters.financialApproved = true;
      }

      if (updatedFilters.statusFilter?.key === "pendingFinance") {
        updatedFilters.ownerApproved = true;
        updatedFilters.financialApproved = false;
      }

      if (updatedFilters.statusFilter?.key === "all") {
        updatedFilters.ownerApproved = undefined;
        updatedFilters.financialApproved = undefined;
      }

      setFilterValues(updatedFilters);
      if (!selfRefundsOnly) {
        updateSearchParams(updatedFilters);
      }
    },
    [selfRefundsOnly, account, location.pathname, updateSearchParams],
  );

  // Atualiza apenas o draft em /approvals, sem disparar fetch
  const handleDraftUpdate = useCallback((newFilterValues: FilterValues) => {
    setDraftFilters(newFilterValues);
  }, []);

  // Commit do draft como filtro aplicado — único ponto que dispara fetch em /approvals
  const handleApplyFilters = useCallback(() => {
    memoizedTriggerFilterUpdate(draftFilters);
  }, [draftFilters, memoizedTriggerFilterUpdate]);

  // Remoção de chip: aplica imediatamente (exceção deliberada ao fluxo de draft)
  const handleChipRemove = useCallback((newFilterValues: FilterValues) => {
    setDraftFilters(newFilterValues);
    memoizedTriggerFilterUpdate(newFilterValues);
  }, [memoizedTriggerFilterUpdate]);

  const handleSortChange = (column: string, direction: "asc" | "desc") => {
    setSort(`${column},${direction}`);
    setCurrentPage(1);
  };

  const handleApproveAll = () => {
    refetchPendingAdvances();
    setOpenModal(true);
  };

  useEffect(() => {
    if (prevPathname.current !== location.pathname || !isInitialized.current) {
      if (!location.search) {
        resetFilters({ useApprovalDefault: true, clearUrl: false });
      }
      prevPathname.current = location.pathname;
      isInitialized.current = true;
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isInitialized.current) return;

    const newFilterValues = { ...filterValues };
    setCurrentPage(1);

    if (location.pathname === "/" || selfRefundsOnly) {
      newFilterValues.collaborator = account ?? null;
      newFilterValues.statusFilter = searchParams.get("statusFilter")
        ? statusFilterOptions[searchParams.get("statusFilter")!]
        : null;
      newFilterValues.startDate = searchParams.get("startDate") ? searchParams.get("startDate")! : null;
      newFilterValues.costCenter = searchParams.get("costCenter") ? searchParams.get("costCenter")! : "";
      newFilterValues.collaboratorName = account?.name ?? "";
      newFilterValues.isAdvance = getAdvanceFilterValue(searchParams.get("isAdvance") ?? "", isMobile);
      setFilterValues(newFilterValues);
      return;
    }

    if (isApprovalsPage) {
      setFilterValues((prev) => {
        const newFilterValues = { ...prev };

        if (searchParams.get("statusFilter")) {
          newFilterValues.statusFilter = statusFilterOptions[searchParams.get("statusFilter")!];
        } else {
          newFilterValues.statusFilter = null;
        }

        if (!location.search) {
          newFilterValues.collaborator = null;
          newFilterValues.collaboratorName = "";
          newFilterValues.ownerName = "";
          newFilterValues.costCenter = "";
          newFilterValues.statusFilter = account?.financialSupport
            ? { ...statusFilterOptions.all }
            : { ...statusFilterOptions.pendingOwner };
          newFilterValues.startDate = null;
          newFilterValues.endDate = null;
          newFilterValues.expenseCategory = null;
          newFilterValues.ownerApproved = undefined;
          newFilterValues.financialApproved = undefined;
          newFilterValues.ownerApprovedDateUntil = null;
          newFilterValues.ownerApprovedDateAfter = null;
          newFilterValues.financialApproveDateUntil = null;
          newFilterValues.financialApproveDateAfter = null;
          newFilterValues.createDateUntil = null;
          newFilterValues.createDateAfter = null;
          newFilterValues.isAdvance = null;
          newFilterValues.clientRefund = null;
        }
        

        setDraftFilters(newFilterValues);
        return newFilterValues;
      });
      return;
    }
  }, [account, searchParams, selfRefundsOnly]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues]);

  useEffect(() => {
    const handleResize = () => {
      const { innerWidth } = window;
      setIsMobile(innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function handleChangePage(_: ChangeEvent<unknown>, page: number): void {
    setCurrentPage(page);
  }

  const hasPendingAdvances = (pendingAdvances?.length ?? 0) > 0;
  const pendingAdvancesCount = pendingAdvances?.length ?? 0;

  const getListName = () => {
    if (location.pathname === "/" && !location.search.includes("isAdvance")) {
      return "Reembolsos";
    }

    if (location.pathname === "/" && location.search.includes("isAdvance")) {
      return "Adiantamentos";
    }

    return "Aprovações pendentes";
  };

  if (isMobile) {
    return (
      <>
        {selfRefundsOnly && !location.search.includes("isAdvance") && (
          <Box
            sx={{
              width: "100%",
              position: "relative",
              marginTop: 0,
              marginBottom: 1,
            }}
          >
            <RefundCycleBanner deadlineDay={4} deadlineHour={8} paymentDay={15} />
          </Box>
        )}

        <DefaultPage>
          {/* REFUND CYCLE BANNER - Mobile colado na topbar */}

          <RefundReportTableMobile
            reportData={filteredReportEntries}
            filterValues={filterValues}
            loggedUser={account}
            title={getListName()}
            isAdvanceView={filterValues.isAdvance}
            onFilterUpdate={(newFilterValues) => {
              triggerFilterUpdate(newFilterValues);
            }}
            collaborators={collaboratorPageData?.content}
            isLoading={
              (isFetchingReportEntries && loadingReportEntries) || isLoadingCollaboratorList
            }
            selfRefundsOnly={selfRefundsOnly}
          />

          {(account?.isApprover || account?.financialSupport) &&
            filteredReportEntries?.content &&
            shouldEnableAllButtons() &&
            hasPendingApproval(filteredReportEntries?.content ?? []) && (
              <div className="flex items-center justify-center fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                <button
                  disabled={isFetchingPendingAdvances}
                  onClick={() => handleApproveAll()}
                  className="w-80 flex items-center justify-center gap-2 bg-[#0C7878] hover:bg-[#0C7878]/90 text-white py-3 px-4 rounded-lg transition-colors"
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {isFetchingPendingAdvances ? <Loading /> : "Aprovar todos"}
                  </Typography>
                </button>
              </div>
            )}
          <Modal
            title="Atenção"
            isOpen={openModal}
            setIsOpen={setOpenModal}
            body={
              <div className="flex flex-col gap-2 mt-2">
                <p className="text-sm text-gray-500">Você deseja aprovar todas as solicitações na tabela?</p>
                {hasPendingAdvances && <PendingAdvancesAlert pendingCount={pendingAdvancesCount} />}
              </div>
            }
            bottom={
              <div className="flex flex-col my-2">
                <div className="flex flex-row justify-around mt-4">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={() =>
                      approveAll(filterValues, {
                        onSuccess: () => {
                          setOpenModal(false);
                        },
                      })
                    }
                  >
                    Sim
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-red-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={() => {
                      setOpenModal(false);
                    }}
                  >
                    Não
                  </button>
                </div>
              </div>
            }
          />
        </DefaultPage>
      </>
    );
  }

  if (isMobile && isSearchPage) {
    return (
      <DefaultPage>
        <Box
          className={`w-[90%] laptop:w-[90%] bg-white ${
            isMobile && "justify-center"
          } m-2 p-4 rounded-md drop-shadow-lg`}
        >
          {" "}
          <div className="flex h-full items-center justify-center">
            Layout mobile em desenvolvimento, por favor acesse por um desktop
          </div>
        </Box>
      </DefaultPage>
    );
  }

  return (
    <>
      {/* REFUND CYCLE BANNER - Full width, colado na topbar */}
      {selfRefundsOnly && (
        <Box
          sx={{
            width: "100vw",
            position: "relative",
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
            marginTop: 0,
          }}
        >
          <RefundCycleBanner deadlineDay={4} deadlineHour={8} paymentDay={15} />
        </Box>
      )}

      <DefaultPage>
        {/* FILTERS - TODO: Refactor this to a component*/}
        <RefundReportFilter
          onFilterUpdate={isApprovalsPage || isSearchPage ? handleDraftUpdate : memoizedTriggerFilterUpdate}
          loggedUser={account}
          appliedFilterValues={filterValues}
          draftFilterValues={isApprovalsPage || isSearchPage ? draftFilters : filterValues}
          selfRefundsOnly={selfRefundsOnly}
          includeAdvances={includeAdvances}
          onApplyFilters={isApprovalsPage || isSearchPage ? handleApplyFilters : undefined}
          onClearFilters={isApprovalsPage || isSearchPage ? handleClearFilters : undefined}
          onChipRemove={isApprovalsPage || isSearchPage ? handleChipRemove : undefined}
        />

        <Box
          sx={{
            width: "90%",
            height: { xs: "75%", "laptop-h": "70%" },
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          {groupByCollaborator && (
            <Box
              sx={{
                width: "24%",
                minWidth: "24%",
                height: { xs: "auto", sm: "95%" },
                backgroundColor: "white",
                borderRadius: "0.375rem",
                boxShadow: 1,
                padding: { xs: 1, sm: 0.5 },
                bgcolor: "background.paper",
                overflow: "hidden",
              }}
            >
              <div className="flex flex-col h-full">
                <div className="overflow-y-auto flex-1">
                  {!isLoadingCollaboratorList && collaboratorPageData ? (
                    <div className="flex flex-col gap-2 p-1">
                      {collaboratorPageData.content.map((item, index) => (
                        <ApprovalCallaborator
                          key={index}
                          data={item}
                          isSelected={
                            filterValues.collaborator?.id === item.id ||
                            filterValues.collaboratorName.toLowerCase() === item.name.toLocaleLowerCase()
                          }
                          onClick={() => {
                            const updated =
                              filterValues?.collaborator?.id === item.id
                                ? { ...filterValues, collaboratorName: "", collaborator: null }
                                : { ...filterValues, collaborator: item };
                            memoizedTriggerFilterUpdate(updated);
                            setDraftFilters(updated);
                            setCurrentPage(1);
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Loading />
                    </div>
                  )}
                  {(collaboratorPageData?.content.length === 0 || (collaboratorPageData?.content ?? []).length === 0) && (
                    <div className="flex h-full items-center justify-center text-sm">Sem dados em colaboradores</div>
                  )}
                </div>
              </div>
            </Box>
          )}

          {/* RIGHT CONTENT TODO: Refactor this to a component*/}
          <Box sx={{ width: "100%", minHeight: "fit-content", overflow: "hidden" }}>
            <RefundReportTable
              collaborators={collaboratorPageData?.content}
              reportData={filteredReportEntries}
              filterValues={filterValues}
              loggedUser={account}
              showCollaboratorColumn={!groupByCollaborator && !selfRefundsOnly}
              title={title}
              showTotalValue={showTotalValue}
              showExportFilesButtons={showExportFilesButtons}
              showSendToButtons={showSendToButtons}
              showAddReportEntryButton={showAddReportEntryButton}
              groupByCollaborator={groupByCollaborator}
              page={currentPage - 1}
              size={20}
              isLoading={loadingReportEntries && isFetchingReportEntries}
              sortColumn={sort.split(",")[0]}
              sortDirection={sort.split(",")[1] as "asc" | "desc"}
              onSortChange={handleSortChange}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                marginTop: 1,
              }}
            >
              {(account?.isApprover || account?.financialSupport) &&
                filteredReportEntries?.content &&
                hasPendingApproval(filteredReportEntries?.content) &&
                shouldEnableAllButtons() && (
                  <>
                    <Button
                      loading={isFetchingPendingAdvances}
                      disabled={isFetchingPendingAdvances}
                      sx={{
                        marginTop: -1,
                      }}
                      onClick={() => {
                        handleApproveAll();
                      }}
                    >
                      Aprovar Todos
                    </Button>

                    <Modal
                      title="Atenção"
                      isOpen={openModal}
                      setIsOpen={setOpenModal}
                      body={
                        <div className="flex flex-col gap-2 mt-2">
                          <p className="text-sm text-gray-500">Você deseja aprovar todas as solicitações na tabela?</p>
                          {hasPendingAdvances && (
                            <PendingAdvancesAlert
                              pendingCount={pendingAdvancesCount}
                              message={`Há ${pendingAdvancesCount} ${
                                pendingAdvancesCount > 1 ? "solicitações" : "solicitação"
                              } de adiantamento pendente${
                                pendingAdvancesCount > 1 ? "s" : ""
                              } para aprovação. Deseja aprovar mesmo assim?`}
                            />
                          )}
                        </div>
                      }
                      bottom={
                        <div className="flex flex-col my-2">
                          <div className="flex flex-row justify-around mt-4">
                            <button
                              type="button"
                              className="inline-flex justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                              onClick={() =>
                                approveAll(filterValues, {
                                  onSuccess: () => {
                                    setOpenModal(false);
                                  },
                                })
                              }
                            >
                              Sim
                            </button>
                            <button
                              type="button"
                              className="inline-flex justify-center rounded-md border border-transparent bg-red-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                              onClick={() => {
                                setOpenModal(false);
                              }}
                            >
                              Não
                            </button>
                          </div>
                        </div>
                      }
                    />
                  </>
                )}

              <Box
                sx={{
                  display: "flex-inline",
                  marginBottom: 2,
                }}
              >
                {filteredReportEntries && filteredReportEntries.totalPages > 1 && (
                  <Pagination
                    count={filteredReportEntries.totalPages}
                    page={currentPage}
                    onChange={handleChangePage}
                    variant="outlined"
                    shape="rounded"
                    color="primary"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DefaultPage>
    </>
  );
};

export default RefundReportView;
